import { Flask } from '@phosphor-icons/react';
import { useNavigate, useParams } from 'react-router-dom';
import { useExperiments } from '../../api/experiments';
import { useIsAdmin } from '../../auth/useAuth';
import { Button } from '../../components/Button';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonLoader } from '../../components/SkeletonLoader';
import { StatusIndicator } from '../../components/StatusIndicator';
import { Table } from '../../components/Table';
import { useProjectBreadcrumbs } from '../../hooks/useProjectBreadcrumbs.js';
import { formatRelativeTime } from '../../utils/format';
import shared from '../shared.module.css';

export function ExperimentListPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const isAdmin = useIsAdmin();

	const { data: experiments, isLoading, error } = useExperiments(id ?? '');
	const breadcrumbs = useProjectBreadcrumbs('Experiments');

	const columns = [
		{ key: 'name', label: 'Name' },
		{ key: 'status', label: 'Status' },
		{ key: 'variants', label: 'Variants' },
		{ key: 'created', label: 'Created' },
	];

	return (
		<div>
			<PageHeader
				title="Experiments"
				breadcrumbs={breadcrumbs}
				actions={
					isAdmin ? (
						<Button variant="primary" onClick={() => navigate(`/projects/${id}/experiments/new`)}>
							New Experiment
						</Button>
					) : undefined
				}
			/>

			<div className={shared.content}>
				<Card>
					{error ? (
						<p className={shared.errorMessage}>
							{error instanceof Error ? error.message : 'Failed to load experiments'}
						</p>
					) : (
						<Table>
							<Table.Header columns={columns} />
							<Table.Body>
								{isLoading ? (
									<SkeletonLoader rows={4} columns={4} />
								) : !experiments || experiments.length === 0 ? (
									<tr>
										<td colSpan={4}>
											<EmptyState
												icon={Flask}
												title="No experiments yet"
												description="Create your first experiment to start measuring."
												action={
													isAdmin
														? {
																label: 'New Experiment',
																onClick: () => navigate(`/projects/${id}/experiments/new`),
															}
														: undefined
												}
											/>
										</td>
									</tr>
								) : (
									experiments.map((exp) => (
										<Table.Row
											key={exp.id}
											onClick={() => navigate(`/projects/${id}/experiments/${exp.id}`)}
										>
											<Table.Cell>{exp.name}</Table.Cell>
											<Table.Cell>
												<StatusIndicator status={exp.status} />
											</Table.Cell>
											<Table.Cell>{exp.variants.length}</Table.Cell>
											<Table.Cell mono>{formatRelativeTime(exp.created_at)}</Table.Cell>
										</Table.Row>
									))
								)}
							</Table.Body>
						</Table>
					)}
				</Card>
			</div>
		</div>
	);
}
